/**
 * Make a "Failed to fetch" on one of our own routes diagnosable.
 *
 * When a same-origin `fetch('/api/...')` never gets a response, every engine
 * rejects with a bare TypeError -- `Failed to fetch` (Chrome), `Load failed`
 * (Safari), `NetworkError when attempting to fetch resource` (Firefox) -- and
 * attaches no cause. The row we file from the catch therefore cannot say
 * whether the visitor navigated away mid-request, was offline, had an
 * extension cut the call, or whether our route was actually unreachable.
 * Those are four different verdicts and the first three are not ours.
 *
 * On 2026-09-01 the shipping-estimate fetch on the product page filed four
 * such rows, all anonymous partner-referral visits (the population most likely
 * to bounce before the estimate lands), and the admin feature-for-partner call
 * filed one whose stack ran through a browser extension's fetch wrapper. None
 * of the rows carried enough to tell the harmless case from the real one.
 *
 * This module records the browser state at the moment the catch runs -- online,
 * page visibility, and whether `pagehide` has already fired -- and, when that
 * state proves the request was cut rather than refused, files the row at info
 * so it does not queue as an application error. A transport failure while the
 * page is visible, online and not unloading keeps the caller's severity: that
 * is the case that might be ours.
 */

import { ErrorSeverity } from './error-tracking';

/**
 * Engine wordings for a fetch that never got a response. Mirrors the list in
 * scripts/error-triage/noise-rules.ts; keep the two in step.
 */
export const TRANSPORT_FETCH_FAILURE_WORDINGS = [
  'Failed to fetch', // Chrome, Edge
  'Load failed', // Safari
  'NetworkError when attempting to fetch resource', // Firefox
] as const;

/** Browser state captured when an own-route fetch failed at the transport level. */
export interface TransportFailureContext {
  /** True when the message is one of the engine transport wordings. */
  transportFailure: boolean;
  /** `navigator.onLine` at the moment of the catch; null where unavailable. */
  online: boolean | null;
  /** `document.visibilityState` at the moment of the catch; null where unavailable. */
  visibility: string | null;
  /**
   * True once `pagehide` has fired for this document (navigation or discard),
   * or within BEFOREUNLOAD_LEAVING_MS of a `beforeunload`.
   */
  unloading: boolean;
}

/**
 * How long after `beforeunload` a cut request still counts as the page leaving.
 *
 * Prod 2026-10-27 11:19:57Z: a partner app's Android WebView left the checkout
 * page; the abandonment tracker heard `beforeunload` and filed "unload", and
 * 120 ms later the listing-status fetch failed with `unloading: false` --
 * `pagehide` had not fired yet, and in that WebView the request was cut first.
 * Bounded, because a `beforeunload` the visitor cancels (the unsaved-changes
 * prompt) leaves the page live, and the clock keeps running while that prompt
 * is up, so the mark has usually lapsed by the time they answer it.
 */
export const BEFOREUNLOAD_LEAVING_MS = 3_000;

/**
 * How long a retry waits when the request was alive across a suspension.
 *
 * Prod 2026-10-29 19:00:08Z: an Android Chrome product page started the
 * shipping estimate, the phone slept, and 93 minutes later the page came back
 * visible and online. The dead request was cut on wake, the retry went out at
 * once and was cut too, and the origin probe a moment after that answered --
 * the network was only still coming back. Waiting this long lets it.
 */
export const RESUME_SETTLE_MS = 1_500;

/**
 * A request still open after this long was not waiting on our route -- no
 * function of ours runs for five minutes -- it was parked with a sleeping
 * device or a frozen tab, even where no lifecycle event said so.
 */
export const SUSPENDED_REQUEST_MS = 5 * 60_000;

let unloading = false;
let beforeUnloadAt: number | null = null;
let lastHiddenAt: number | null = null;
let lastResumedAt: number | null = null;
let watching = false;

/** True when the page is leaving: `pagehide` fired, or `beforeunload` just did. */
function isLeaving(): boolean {
  return unloading || (beforeUnloadAt !== null && Date.now() - beforeUnloadAt < BEFOREUNLOAD_LEAVING_MS);
}

/**
 * Start tracking `pagehide` / `pageshow` on the window. Idempotent. Armed when
 * this module loads in a browser (see the call below) -- it used to be armed
 * lazily by the first catch, which meant the listener did not exist yet when a
 * document's first request was cut by navigation, so that row always read
 * `unloading: false`. From 2026-09-01 to 09-23 every shipping-estimate row said
 * exactly that, and the flag could not have said anything else.
 * `pageshow` clears the flag because a bfcache restore brings the same
 * document back after `pagehide` and its later fetches are live again.
 */
export function watchPageUnload(win: Window | undefined = typeof window === 'undefined' ? undefined : window): void {
  if (watching || !win || typeof win.addEventListener !== 'function') return;
  watching = true;
  win.addEventListener('pagehide', () => {
    unloading = true;
  });
  win.addEventListener('beforeunload', () => {
    beforeUnloadAt = Date.now();
  });
  win.addEventListener('pageshow', () => {
    unloading = false;
    beforeUnloadAt = null;
  });
  const doc = win.document;
  if (doc && typeof doc.addEventListener === 'function') {
    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'hidden') lastHiddenAt = Date.now();
    });
    // Chrome's page lifecycle: a frozen tab runs nothing between these two.
    doc.addEventListener('freeze', () => {
      lastHiddenAt = Date.now();
    });
    doc.addEventListener('resume', () => {
      lastResumedAt = Date.now();
    });
  }
}

/**
 * True when the page was hidden, frozen or asleep at some point after
 * `startedAt` -- a request that spanned that was cut by the device, and the
 * network may not be back yet when the catch runs.
 */
export function pageSuspendedSince(startedAt: number): boolean {
  if (lastHiddenAt !== null && lastHiddenAt >= startedAt) return true;
  if (lastResumedAt !== null && lastResumedAt >= startedAt) return true;
  return Date.now() - startedAt >= SUSPENDED_REQUEST_MS;
}

/**
 * Before retrying a cut request, give a waking device's network time to come
 * back when the request lived through a suspension. Resolves true when it
 * waited. A request cut on a page that never slept retries at once, as before.
 */
export async function settleBeforeRetry(startedAt: number): Promise<boolean> {
  if (!pageSuspendedSince(startedAt)) return false;
  await new Promise<void>((resolve) => setTimeout(resolve, RESUME_SETTLE_MS));
  return true;
}

// Arm before any caller's request starts. A no-op on the server.
watchPageUnload();

/** True when `error` is an engine transport rejection rather than an answered request. */
export function isTransportFetchFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return TRANSPORT_FETCH_FAILURE_WORDINGS.some((wording) =>
    message.includes(wording.toLowerCase())
  );
}

/** Snapshot the transport-relevant browser state for an error row's `extra`. */
export function transportFailureContext(error: unknown): TransportFailureContext {
  watchPageUnload();
  // Every browser has a navigator, and so does Node since 21 (without onLine).
  const doc = typeof document === 'undefined' ? undefined : document;
  return {
    transportFailure: isTransportFetchFailure(error),
    online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
    visibility: typeof doc?.visibilityState === 'string' ? doc.visibilityState : null,
    unloading: isLeaving(),
  };
}

/**
 * Severity for a failed own-route fetch. A transport rejection while the page
 * is unloading or the browser is offline is a request the visitor's browser
 * cut, not one our route refused; it files at info so the triage queue does
 * not carry it. Anything else keeps `fallback` -- including a transport failure
 * on a visible, online page, which is the one case worth a human's look.
 */
export function severityForOwnFetchFailure(
  error: unknown,
  fallback: ErrorSeverity,
  context: TransportFailureContext = transportFailureContext(error)
): ErrorSeverity {
  if (!context.transportFailure) return fallback;
  if (context.unloading || context.online === false) return ErrorSeverity.INFO;
  return fallback;
}

/**
 * True when a failed own-route fetch is worth one more attempt: an engine
 * transport rejection on a page that is online and not unloading. That is the
 * case the 2026-09-24 rows proved real -- visible, online, not unloading, cut
 * in 233ms and again 20 seconds later on the same listing -- and the one a
 * single retry can still turn into an answer. A page that is leaving or
 * offline would only be cut again.
 */
export function shouldRetryTransportFailure(
  error: unknown,
  context: TransportFailureContext = transportFailureContext(error)
): boolean {
  return context.transportFailure && !context.unloading && context.online !== false;
}

/** Same-origin route that answers without touching a datastore. */
export const OWN_ORIGIN_PROBE_URL = '/api/health';

/**
 * After a retried request is cut a second time, ask whether this browser can
 * reach our origin at all. True means our origin answered and only the failing
 * route was cut -- an extension blocking that URL, or the route itself dying
 * before it answers. False means nothing of ours gets through from here. Null
 * where fetch does not exist.
 */
export async function probeOwnOrigin(
  fetchImpl: typeof fetch | undefined = typeof fetch === 'undefined' ? undefined : fetch
): Promise<boolean | null> {
  if (!fetchImpl) return null;
  try {
    const response = await fetchImpl(OWN_ORIGIN_PROBE_URL, { cache: 'no-store' });
    return response.ok;
  } catch {
    return false;
  }
}

/** Test seam: forget the listener and the unload flag. */
export function resetTransportFailureStateForTests(): void {
  unloading = false;
  beforeUnloadAt = null;
  lastHiddenAt = null;
  lastResumedAt = null;
  watching = false;
}
